import { buildHeaders } from './headers.js';
import { isNonceReplayBody } from './errors.js';
import type { AuthProvider, RetryPolicy } from './types.js';

// A replay 401 gets its own allowance, separate from `retry.maxAttempts`. That budget is for
// transport faults and congestion; a nonce replay is neither. `maxAttempts` defaults to 2, so
// charging a same-millisecond tie against it would leave a genuine 429 immediately afterwards
// with nothing left to retry with. Bounded so a keypair being hammered from many contexts
// degrades instead of spinning: total requests stay capped at `maxAttempts + this`.
const MAX_NONCE_REPLAY_RESIGNS = 2;

export interface RequestDeps {
  fetchFn: typeof globalThis.fetch;
  auth: AuthProvider;
  retry: RetryPolicy;
  // Monotonic per-Gateway nonce source (W1): never returns the same value twice, so an
  // app-start burst from one keypair can't collide into a 401 replay.
  nextNonce: () => string;
  sleep: (ms: number) => Promise<void>;
}

// Signs and sends a request, retrying a thrown fetch (DNS/TCP/TLS) and a 429/503 up to the
// budget, plus a nonce replay on its own allowance. Shared by AggregateClient and the Gateway's
// own routes so the signing + backoff discipline lives in exactly one place.
export async function signedRequestWithRetry(deps: RequestDeps, url: string, init: RequestInit): Promise<Response> {
  let attempt = 0;
  let replayResigns = 0;
  let lastError: unknown;
  while (attempt < deps.retry.maxAttempts) {
    attempt++;
    const nonce = deps.nextNonce();
    const creds = await deps.auth(nonce);
    // Caller headers first so signed headers always win (a caller passing a wrong X-Nonce or
    // Content-Type can't shadow the signed envelope).
    const headers = { ...(init.headers as Record<string, string> | undefined), ...buildHeaders(creds, nonce) };
    let response: Response;
    try {
      response = await deps.fetchFn(url, { ...init, headers });
    } catch (e: unknown) {
      lastError = e;
      if (attempt >= deps.retry.maxAttempts) throw e;
      await deps.sleep(deps.retry.defaultBackoffMs);
      continue;
    }
    // The recoverable 401. Two Gateway instances sharing one keypair emitted the same
    // (client_id, nonce); the gateway kept the first and refused this one. Re-sign: nextNonce()
    // returns max(last + 1, now), so the retry carries a strictly greater nonce AND leaves this
    // instance's counter past the peer's, so the pair stops colliding on later reads.
    //
    // Cloned, not consumed. The caller runs throwForStatus on whatever is returned and that reads
    // the body to tell a replay from a real auth failure; draining it here would leave nothing.
    if (response.status === 401 && replayResigns < MAX_NONCE_REPLAY_RESIGNS) {
      let replay = false;
      try {
        replay = isNonceReplayBody(await response.clone().json());
      } catch {
        replay = false;
      }
      if (replay) {
        replayResigns++;
        // Does not draw from the transport budget, and does not sleep: this is a
        // same-millisecond tie, not congestion, and a 1s backoff would turn a hidden fault into
        // a visible stall. Termination comes from MAX_NONCE_REPLAY_RESIGNS, not from `attempt`.
        attempt--;
        try { await response.text(); } catch { /* ignore drain failure */ }
        continue;
      }
    }
    if ((response.status === 429 || response.status === 503) && attempt < deps.retry.maxAttempts) {
      const retryAfter = response.headers.get('Retry-After');
      const seconds = retryAfter === null ? NaN : Number(retryAfter);
      const waitMs = Number.isFinite(seconds) ? seconds * 1000 : deps.retry.defaultBackoffMs;
      try { await response.text(); } catch { /* ignore drain failure */ }
      await deps.sleep(waitMs);
      continue;
    }
    return response;
  }
  // Loop exits only via continue; this is unreachable but TS requires a terminator.
  throw lastError ?? new Error('retry budget exhausted');
}
